<template>
  <v-row class="ma-0 pa-0 pricingDetailsRow">
    <div v-show="!filterExpanded" class="filterExpandBtnContainer">
      <v-btn fab x-small color="primary" @click="updateFilterExpanded">
        <i class="fal fa-chevron-right"></i>
      </v-btn>
    </div>
    <!-- filter -->
    <v-col v-show="filterExpanded" class="col-3 pa-0 filterColumn">
      <v-card tile class="filterCard" outlined>
        <v-card-text class="px-0 pt-0">
          <product-definition-table
            v-if="products && currentRange"
            hide-depending-product-definitions
            alignment="vertical"
            :products="products"
            is-single-select
            :filterExpanded.sync="filterExpanded"
            :store-set-selected-definitions-path="'DetailsPricing/setSelectedDefinitions'"
            :store-get-selected-definitions-path="'DetailsPricing/selectedDefinitions'"
            :store-add-definition-path="'DetailsPricing/addSelectedDefinition'"
            :store-remove-definition-path="'DetailsPricing/removeSelectedDefinition'"
          />
        </v-card-text>
      </v-card>
      <div class="filterExpandBtnContainerCollapsed">
        <v-btn fab x-small color="primary" @click="updateFilterExpanded">
          <i class="fal fa-chevron-left"></i>
        </v-btn>
      </div>
    </v-col>

    <!-- charts -->
    <v-col
      class="pt-0 chartContainer"
      :class="filterExpanded ? 'col-9' : 'col-12 ml-2'"
    >
      <!-- daily base rate -->
      <v-row>
        <v-col class="col-12">
          <v-card outlined>
            <pricing-title-helper>
              <span v-if="$store.getters.isActivePricingModel('winter_1')">
                {{ $t('pricingDetails.dailyBaseRate') }}
              </span>
              <span v-if="$store.getters.isActivePricingModel('winter_3')">
                {{ $t('pricingDetails.cashDeskPrice') }}
              </span>
            </pricing-title-helper>
            <v-card-text class="py-0">
              <!-- Model-I -->
              <daily-base-rate-chart
                v-if="$store.getters.isActivePricingModel('winter_1')"
                :products="products"
                :daily-base-rates="dailyBaseRates"
                :date-range="currentRange"
                @selected="openModal('baseRates', $event)"
              >
              </daily-base-rate-chart>
              <daily-base-rate-overwrite-modal
                ref="baseRates"
                :daily-base-rate-overwrite-metadata="
                  dailyBaseRateOverwriteMetadata
                "
                @updated="updateChartData('baseRates')"
              >
              </daily-base-rate-overwrite-modal>

              <!-- Model-III -->
              <cash-desk-price-chart
                v-if="$store.getters.isActivePricingModel('winter_3')"
                :date-range="currentRange"
                :selected-definitions="selectedDefinitions"
                :cash-desk-prices="cashDeskPrices"
                :point-radius="0"
                @selected="openModal('cashDeskMod', $event)"
              ></cash-desk-price-chart>
              <cash-desk-prices-chart-overwrite-modal
                ref="cashDeskMod"
                :cash-desk-price-overwrite-metadata="
                  cashDeskPriceOverwriteMetadata
                "
                @updated="updateChartData('deskPrices', selectedDefinitions)"
              >
              </cash-desk-prices-chart-overwrite-modal>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- multi day discount -->
      <v-row v-if="!$store.getters.isActivePricingModel('winter_3')">
        <v-col class="col-12">
          <v-card outlined>
            <pricing-title-helper :filter-expanded.sync="filterExpanded">
              {{ $t('pricingDetails.multiDayDiscount') }}
            </pricing-title-helper>
            <v-card-text class="py-0">
              <multi-day-discount-chart
                :date-range="currentRange"
                :multi-day-discounts="multiDayDiscounts"
                @selected="openModal('multiDayMod', $event)"
              >
              </multi-day-discount-chart>
              <multi-day-discount-overwrite-modal
                ref="multiDayMod"
                :multi-day-discount-overwrite-metadata="
                  multiDayDiscountOverwriteMetadata
                "
                @updated="updateChartData('multiDays', selectedDefinitions)"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Current / Maximum Early Booking Discount -->
      <v-row>
        <v-col class="col-12">
          <v-card outlined>
            <pricing-title-helper :filter-expanded.sync="filterExpanded">
              {{ $t('pricingDetails.maximumEarlyBooking') }}
            </pricing-title-helper>
            <v-card-text class="py-0">
              <early-booking-chart
                :date-range="currentRange"
                :maximum-early-booking-discounts="maximumEarlyBookingDiscounts"
                @selected="openModal('earlyBooking', $event)"
              >
              </early-booking-chart>
              <early-booking-overwrite-modal
                v-if="$store.getters.isActivePricingModel('winter_1')"
                ref="earlyBookingMod"
                :early-booking-overwrite-metadata="
                  maximumEarlyBookingDiscountOverwriteMetadata
                "
                @updated="
                  updateChartData('maxEarlyBookings', selectedDefinitions)
                "
              >
              </early-booking-overwrite-modal>
              <early-booking-overwrite-modal-winter3
                v-if="$store.getters.isActivePricingModel('winter_3')"
                ref="earlyBookingMod3"
                :early-booking-overwrite-metadata="
                  maximumEarlyBookingDiscountOverwriteMetadata
                "
                @updated="
                  updateChartData('maxEarlyBookings', selectedDefinitions)
                "
              >
              </early-booking-overwrite-modal-winter3>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
    <v-col
      class="dateRangeSliderContainer"
      :class="filterExpanded ? 'col-9' : 'col-12'"
    >
      <date-range-slider
        :currentRange="currentRange"
        :dateRange="dateRange"
        @currentDateChange="updateDateRange"
      />
    </v-col>
  </v-row>
</template>

<script>
import ProductDefinitionTable from '@/components-internal/productDefinitionTable/ProductDefinitionTable'
import PricingTitleHelper from '@/components-internal/productDefinitionTable/PricingTitleHelper'
import DailyBaseRateChart from '@/components-internal/charts/DailyBaseRateChart'
import DailyBaseRateOverwriteModal from '@/components-internal/charts/DailyBaseRateOverwriteModal'
import CashDeskPriceChart from '@/components-internal/charts/CashDeskPricesChart'
import CashDeskPricesChartOverwriteModal from '@/components-internal/charts/CashDeskPricesChartOverwriteModal'
import MultiDayDiscountOverwriteModal from '@/components-internal/charts/MultiDayDiscountOverwriteModal'
import MultiDayDiscountChart from '@/components-internal/charts/MultiDayDiscountChart'
import EarlyBookingChart from '@/components-internal/charts/EarlyBookingChart'
import EarlyBookingOverwriteModal from '@/components-internal/charts/EarlyBookingOverwriteModal'
import EarlyBookingOverwriteModalWinter3 from '@/components-internal/charts/EarlyBookingOverwriteModalWinter3'
import DateRange from '@/classes-shared/dates/DateRange'
import { mapState } from 'vuex'
import DateRangeSlider from '@/components-internal/dateRange/DateRangeSlider'

export default {
  name: 'PricingDetails',
  components: {
    EarlyBookingChart,
    MultiDayDiscountChart,
    DailyBaseRateChart,
    PricingTitleHelper,
    ProductDefinitionTable,
    CashDeskPriceChart,
    CashDeskPricesChartOverwriteModal,
    MultiDayDiscountOverwriteModal,
    EarlyBookingOverwriteModal,
    EarlyBookingOverwriteModalWinter3,
    DailyBaseRateOverwriteModal,
    DateRangeSlider,
  },

  data() {
    return {
      filterExpanded: true,
      dateRange: new DateRange(),
      currentRange: new DateRange(),
    }
  },
  computed: {
    ...mapState('DetailsPricing', [
      'selectedDefinitions',
      'cashDeskPrices',
      'dailyBaseRates',
      'multiDayDiscounts',
      'maximumEarlyBookingDiscounts',
      'cashDeskPriceOverwriteMetadata',
      'multiDayDiscountOverwriteMetadata',
      'maximumEarlyBookingDiscountOverwriteMetadata',
      'dailyBaseRateOverwriteMetadata',
    ]),
    products() {
      return this.$store.getters.getProducts()
    },
  },
  watch: {
    selectedDefinitions: {
      handler: function (newDefinitions) {
        this.updateChartData('all', newDefinitions)
      },
    },
  },

  mounted() {
    this.$store.commit('DetailsPricing/reset')
    const today = new Date()
    const minSeasonStart = this.products.getFirstSeasonStart()
    const seasonStart = today > minSeasonStart ? today : minSeasonStart
    this.dateRange.update(seasonStart, this.products.getMaxSeasonEnd())
    this.currentRange.update(seasonStart, this.products.getMaxSeasonEnd())
  },

  methods: {
    updateDateRange(range) {
      this.currentRange.update(range[0], range[1])
    },
    updateFilterExpanded() {
      this.filterExpanded = !this.filterExpanded
    },
    async openModal(type, chartJsEvent) {
      if (chartJsEvent.length > 0) {
        if (type === 'cashDeskMod') {
          await this.$store.dispatch(
            'DetailsPricing/updateCashDeskPriceOverwriteMetadata',
            chartJsEvent
          )
          this.$refs.cashDeskMod.open()
        } else if (type === 'multiDayMod') {
          await this.$store.dispatch(
            'DetailsPricing/updateMultiDayDiscountOverwriteMetadata',
            chartJsEvent
          )
          this.$refs.multiDayMod.open()
        } else if (type === 'earlyBooking') {
          await this.$store.dispatch(
            'DetailsPricing/updateEarlyBookingOerwriteMetadata',
            chartJsEvent
          )
          if (this.$store.getters.isActivePricingModel('winter_3')) {
            this.$refs.earlyBookingMod3.open()
          } else {
            this.$refs.earlyBookingMod.open()
          }
        } else if (type === 'baseRates') {
          await this.$store.dispatch(
            'DetailsPricing/updateDailyBaseRateOverwriteMetadata',
            chartJsEvent
          )
          this.$refs.baseRates.open()
        }
      }
    },
    updateChartData(type = 'all', definitions = []) {
      const ids = definitions.map((prodDef) => prodDef.item.id)
      const seasonDateRange = new DateRange(
        this.products.getFirstSeasonStart(),
        this.products.getMaxSeasonEnd()
      )
      if (ids.length > 0) {
        if (type === 'all' || type === 'deskPrices') {
          this.$store.dispatch('DetailsPricing/fetchCashDeskPrices', {
            from: seasonDateRange.startDate,
            to: seasonDateRange.endDate,
            productDefIds: ids,
          })
        }
        if (type === 'all' || type === 'multiDays') {
          this.$store.dispatch('DetailsPricing/fetchMultiDayDiscounts', {
            from: seasonDateRange.startDate,
            to: seasonDateRange.endDate,
            productDefIds: ids,
          })
        }
        if (type === 'all' || type === 'maxEarlyBookings') {
          this.$store.dispatch(
            'DetailsPricing/fetchMaximumEarlyBookingDiscounts',
            {
              from: seasonDateRange.startDate,
              to: seasonDateRange.endDate,
              productDefIds: ids,
            }
          )
        }
      }
      if (type === 'all' || type === 'baseRates') {
        this.$store.dispatch('DetailsPricing/fetchDailyBaseRates', {
          from: seasonDateRange.startDate,
          to: seasonDateRange.endDate,
        })
      }
    },
  },
}
</script>
<style scoped>
.filterExpandBtnContainer {
  position: absolute;
  top: 80px;
  left: -15px;
  z-index: 5;
}

.filterExpandBtnContainerCollapsed {
  position: absolute;
  top: 15px;
  right: -15px;
  z-index: 2;
}

.filterColumn {
  height: 100%;
  position: relative;
}

.chartContainer {
  overflow: auto;
  height: calc(100% - 56px);
  z-index: 1;
}

.dateRangeSliderContainer {
  position: absolute;
  box-shadow: 0px -5px 10px -5px rgba(0, 0, 0, 0.2) !important;
  bottom: 0px;
  right: 0px;
  background: white;
  z-index: 2;
}

.pricingDetailsRow {
  height: calc(100vh - 65px);
  overflow: hidden;
}

.filterCard {
  overflow: auto;
  height: 100%;
}
</style>
